import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core'
import { BaseListing } from '../_base-listing/base-listing'
import { ListingItem } from 'depoto-core/src/models'
import { ExportsComponent } from '../exports/exports.component'
import { CoreService } from '../../services'
import { Order } from 'depoto-core/src/entities'
import { ActivatedRoute } from '@angular/router'
import { ordersColumns } from '../../utils/definitions.util'
import { GeoConfig, GeoListItemType, OptionsConfig } from '../../configs'
import { injectInfiniteQuery, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental'
import { CheckoutService } from '../../services/entity-services/checkout.service'
import { DepotService } from '../../services/entity-services/depot.service'
import { PaymentService } from '../../services/entity-services/payment.service'
import { TagService } from '../../services/entity-services/tag.service'
import { OrderService } from '../../services/entity-services/order.service'
import { UserService } from '../../services/entity-services/user.service'
import { createSubmittedFilterState, SchemaUtil, withDateBoundary } from '../../utils'
import { CompanyCarrierService } from '../../services/entity-services/companyCarrier.service'
import { ModalLoadingComponent } from '../../components/modal-components/modal-loading/modal-loading.component'
import { TranslateService } from '@ngx-translate/core'
import { trimInfiniteQueryToFirstPage } from '../../services/query-cache.service'

const DEFAULT_FILTERS = {
  fulltext: '',
  dateCreated: {
    right_date: '',
    left_date: '',
  },
  processStatus: 'all',
  // todo customer for salesman check
  customer: undefined,
  reservationNumber: null,
  billNumber: null,
  package: '',
  item: '',
  tags: null,
  tagsNegate: null,
  status: 'all',
  hasAvailableProducts: 'all',
  carriers: [] as string[],
  payments: null,
  checkouts: [], // all user's checkouts, setting in constructor
  depots: null,
  isPaid: 'all',
  priority: null,
  dateExpedition: {
    right_date: '',
    left_date: '',
  },
  createdBy: null,
  dateBill: {
    right_date: '',
    left_date: '',
  },
  dateTax: {
    right_date: '',
    left_date: '',
  },
  dateDue: {
    right_date: '',
    left_date: '',
  },
  shippingCountry: null,
  externalId: null,
}

const ORDER_DATE_FILTER_KEYS = ['dateCreated', 'dateExpedition', 'dateBill', 'dateTax', 'dateDue'] as const

function toDateInputValue(value: unknown): unknown {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.substring(0, 10) : value
}

function normalizeCarrierIds(value: unknown): string[] {
  const values = Array.isArray(value) ? value : [value]
  return [
    ...new Set(
      values
        .map(item => (item && typeof item === 'object' ? item.id : item))
        .filter((id): id is string => typeof id === 'string' && id.trim() !== '' && id !== 'all')
    ),
  ]
}

function hydrateOrderFilters(value: unknown): typeof DEFAULT_FILTERS {
  const overrides = value && typeof value === 'object' && !Array.isArray(value) ? value : {}
  const filters: Record<string, any> = { ...structuredClone(DEFAULT_FILTERS), ...overrides }
  filters.carriers = normalizeCarrierIds('carriers' in overrides ? filters.carriers : filters.carrier)
  delete filters.carrier

  for (const key of ORDER_DATE_FILTER_KEYS) {
    const range = filters[key]
    if (!range || typeof range !== 'object' || Array.isArray(range)) continue

    filters[key] = {
      ...structuredClone(DEFAULT_FILTERS[key]),
      ...range,
      left_date: toDateInputValue(range.left_date),
      right_date: toDateInputValue(range.right_date),
    }
  }

  return filters as typeof DEFAULT_FILTERS
}

@Component({
  selector: 'app-orders',
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OrdersComponent extends BaseListing implements OnInit, OnDestroy {
  private orderService = inject(OrderService)
  private checkoutService = inject(CheckoutService)
  private depotService = inject(DepotService)
  private paymentService = inject(PaymentService)
  private tagService = inject(TagService)
  private companyCarrierService = inject(CompanyCarrierService)
  private userService = inject(UserService)
  private translateService = inject(TranslateService)
  private queryClient = injectQueryClient()

  // @ViewChild('baseListingComponent', { static: true }) baseListingComponent: BaseListingComponent
  @ViewChild('exportsModal') exportsModal: ExportsComponent
  @ViewChild('loadingModal') loadingModal: ModalLoadingComponent

  private refreshAttemptOrdersExport = 0

  isPaidOptions: string[] = ['all', 'yes', 'no']
  priorityOptions: { id: number | string; name: string }[] = [
    { id: '', name: '' },
    { id: 0, name: 'priority.lowest' },
    { id: 1, name: 'priority.low' },
    { id: 10, name: 'priority.normal' },
    { id: 20, name: 'priority.high' },
    { id: 30, name: 'priority.highest' },
  ] // todo declare only on one place
  shippingCountriesOptions: GeoListItemType[] = [
    { name: 'all', code: 'all' },
    ...GeoConfig.getCountries(this.translateService.currentLang),
  ]
  statusTypesOptions: string[] = ['all', 'bill', 'reservation']
  processStatesOptions: string[] = [
    'all',
    'received',
    'picking',
    'packing',
    'packed',
    'dispatched',
    'delivered',
    'returned',
    'picking_error',
    'cancelled',
  ]
  canCreate = signal(
    this.core.services.user.hasRole('ROLE_ORDER_CREATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
  )
  public hasAvailableProductsOptions = ['', 'yes', 'no']

  listingColumns = signal(ordersColumns)
  selectedColumns: WritableSignal<ListingItem[]> = signal(this.listingColumns().filter(col => col.isDefault))

  filtersFromSellItem = signal(false)
  subscription

  filtersExpanded = signal(false)
  readonly defaultFilters = DEFAULT_FILTERS
  readonly advancedFilterKeys = [
    'reservationNumber',
    'billNumber',
    'externalId',
    'package',
    'item',
    'tags',
    'tagsNegate',
    'status',
    'hasAvailableProducts',
    'carriers',
    'payments',
    'checkouts',
    'depots',
    'isPaid',
    'dateExpedition',
    'priority',
    'createdBy',
    'dateBill',
    'dateTax',
    'dateDue',
    'shippingCountry',
  ]

  filters = signal(structuredClone(DEFAULT_FILTERS))

  readonly queryFilters = computed(() => {
    const filtersWithValues = {}
    for (const [key, value] of Object.entries(this.filters())) {
      // if it has value
      if (
        value !== undefined &&
        value !== null &&
        value !== false &&
        (value !== 0 || key === 'priority') &&
        value !== '' &&
        value !== 'all' &&
        (!Array.isArray(value) || (Array.isArray(value) && value?.length))
      ) {
        if (value === 'yes' || value === 'no') {
          // boolean
          filtersWithValues[key] = value === 'yes'
        } else if (Array.isArray(value) && value[0]?.id) {
          // array
          filtersWithValues[key] = value.map(object => object?.id)
        } else if ((key === 'billNumber' || key === 'reservationNumber') && !isNaN(+value)) {
          // array
          filtersWithValues[key] = Number(value)
        } else if (key === 'customer') {
          // object with id
          if (value && (value as any)?.id) {
            filtersWithValues[key] = (value as any)?.id
          }
        } else if (key === 'priority') {
          // The native select emits a primitive string, while persisted filters
          // may contain the original option object. Normalize both shapes and
          // preserve priority 0 ("lowest"), which is a valid filter value.
          const rawPriority = typeof value === 'object' ? (value as any)?.id : value
          const priority = Number(rawPriority)
          if (rawPriority !== undefined && rawPriority !== null && rawPriority !== '' && Number.isFinite(priority)) {
            filtersWithValues[key] = priority
          }
        } else if (
          key === 'dateCreated' ||
          key === 'dateExpedition' ||
          key === 'dateBill' ||
          key === 'dateDue' ||
          key === 'dateTax'
        ) {
          // date object
          // if ((value && (value as any).left_date?.length) || (value as any).right_date?.length) {
          //   if ((value as any).right_date?.length) {
          //     filtersWithValues[key] = {
          //       left_date: (value as any).left_date,
          //       right_date: (value as any).right_date + 'T23:59:59',
          //     }
          //   } else {
          //     filtersWithValues[key] = value
          //   }
          // }

          const left = (value as any)?.left_date
          const right = (value as any)?.right_date
          if ((left && left.length) || (right && right.length)) {
            filtersWithValues[key] = {
              ...(left?.length && {
                left_date: left,
              }),
              ...(right?.length && {
                right_date: withDateBoundary(right, 'end'),
              }),
            }
          }
        } else if (key === 'shippingCountry') {
          // transform shippingCountry to shippingCountries array
          const typedValue = value as GeoListItemType
          if (typedValue) {
            filtersWithValues['shippingCountries'] = [value]
          }
        } else {
          // other
          filtersWithValues[key] = value
        }
      }
    }
    if (
      !this.checkoutService.doesUserHaveAllCheckouts(this.queryCheckouts.data()) &&
      this.core.services.user.user?.checkouts?.length &&
      !filtersWithValues['checkouts']?.length
    ) {
      filtersWithValues['checkouts'] = [...this.queryCheckouts.data().map(c => c?.id)]
    }
    if (
      !this.depotService.doesUserHaveAllDepots(this.queryDepotsOptions.data()) &&
      this.core.services.user.user?.depots?.length &&
      !filtersWithValues['depots']?.length
    ) {
      filtersWithValues['depots'] = [...this.queryDepotsOptions.data().map(d => d?.id)]
    }
    return filtersWithValues
  })

  private readonly submittedFilters = createSubmittedFilterState<typeof DEFAULT_FILTERS, Record<string, any>>({
    saveView: filters => {
      if (!this.filtersFromSellItem()) this.saveSettings(filters)
    },
    refetch: () => this.refetchOrdersFromFirstPage(),
  })
  readonly submittedQueryFilters = computed(() => this.submittedFilters.snapshot()?.request ?? {})
  readonly submittedViewFilters = computed(() => this.submittedFilters.snapshot()?.view ?? this.filters())

  sortProp = signal('dateCreated')

  sortDir = signal('desc')
  private readonly ordersQueryKey = computed(
    () =>
      [
        'orders',
        'list',
        {
          columns: this.selectedColumns().map(column => column.name),
          sortProp: this.sortProp(),
          sortDir: this.sortDir(),
          filters: this.submittedFilters.snapshot()?.request ?? {},
        },
      ] as const
  )
  // todo readonly queryKey = computed(() => [
  //   'checkouts',
  //   {
  //     sortProp: this.sortProp(),
  //     sortDir: this.sortDir(),
  //   },
  // ]);

  // orders = computed(() => {
  //   return this.queryOrders
  //     .data()
  //     ?.pages?.map(p => p.items)
  //     ?.flat()
  //     .map(o => {
  //       return {...o, isChecked: false}
  //     })
  // })
  checkedMap = signal<Record<string, boolean>>({})
  private readonly orderItems = computed<Order[]>(
    () => this.queryOrders.data()?.pages?.flatMap(page => page?.items ?? []) ?? []
  )
  // orders = computed(() => {
  //   const items = this.items()
  //   const map = this.checkedMap?.()
  //
  //   if (!map) {
  //     // no checkedMap passed → behave normally
  //     return items
  //   }
  //
  //   // Orders mode: attach isChecked
  //   return items.map(item => ({
  //     ...item,
  //     isChecked: map[item.id] ?? false
  //   }))
  // })

  // Reactive select-all header state for Orders
  areAllOrdersChecked = signal<boolean>(false)

  checkedOrders = computed(() => {
    if (this.areAllOrdersChecked()) return []
    const map = this.checkedMap()
    return this.orderItems().filter(order => !!map[order?.id])
  })

  totalItems = computed(() => this.queryOrders.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  readonly queryCheckouts = injectQuery(() => ({
    queryKey: ['checkouts', 'options'],
    queryFn: async () => {
      return await this.checkoutService
        .getCheckouts({ page: 1, sort: 'name', direction: 'asc' })
        .then(result => result.items)
    },
    select: data => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.checkouts?.length) {
        // TODO CORE USER
        return []
      } else if (
        !this.checkoutService.doesUserHaveAllCheckouts(data as any[]) &&
        this.core.services.user.user?.checkouts?.length // TODO CORE USER
      ) {
        return this.core.services.user.user.checkouts // TODO CORE USER
      } else {
        return data as any[]
      }
    },
  }))

  readonly queryDepotsOptions = injectQuery(() => ({
    queryKey: ['depots', 'options'],
    queryFn: async () => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.depots?.length) {
        // TODO CORE USER
        return []
      } else {
        return await this.depotService
          .getDepots(
            {
              sort: 'name',
              direction: 'asc',
            },
            { id: null, name: null, enabled: null }
          )
          .then(result => result.items)
      }
    },
    select: data => {
      if (
        !this.depotService.doesUserHaveAllDepots(data as any[]) &&
        this.core.services.user.user?.depots?.length // TODO CORE USER
      ) {
        return this.core.services.user.user.depots // TODO CORE USER
      } else {
        return data as any[]
      }
    },
  }))

  readonly queryPayments = injectQuery(() => ({
    queryKey: ['payments', 'options'],
    queryFn: async () => {
      return await this.paymentService.getPayments({}, { id: null, name: null, type: { id: null } }).then(result => {
        return result.items
      })
    },
  }))

  readonly queryTags = injectQuery(() => ({
    queryKey: ['tags', 'options'],
    queryFn: async () => {
      return await this.tagService
        .getTags({ sort: 'name', direction: 'asc' }, { id: null, name: null, type: null })
        .then(result => {
          return result.items
        })
    },
    select: data =>
      (data as any[])
        .filter(tag => tag.type === 'order')
        .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0)),
  }))

  readonly queryCompanyCarriers = injectQuery(() => ({
    queryKey: ['companyCarriers', 'options'],
    queryFn: async () => {
      return await this.companyCarrierService
        .getCompanyCarriers({}, { id: null, carrier: SchemaUtil.carrier })
        .then(result => {
          return [
            ...new Map(
              (result?.items ?? []).map(companyCarrier => companyCarrier.carrier).map(d => [d.id, d])
            ).values(),
          ]
        })
    },
    select: data => {
      const sorted = Array.isArray(data)
        ? [...(data as any)].sort((a, b) => {
            if (a.name < b.name) return -1
            if (a.name > b.name) return 1
            return 0
          })
        : []
      return [{ id: 'all', name: 'all' }, ...sorted]
    },
  }))

  readonly carrierFilterOptions = computed(() =>
    (this.queryCompanyCarriers.data() ?? []).filter(carrier => carrier.id !== 'all')
  )

  areAllListedOrdersChecked = computed(() => {
    // todo check if right ?
    const map = this.checkedMap()
    return this.orderItems().every(order => !!map[order?.id])
    // const bla = structuredClone(this.orders())
    // return bla.every(o => o.isChecked = true)todo
    // return false
  })
  // areAllOrdersChecked = computed(() => {
  //   return this.areAllOrdersCheckboxChecked() ||
  // })

  // checkedOrders = computed(() => {
  //   if (this.orders()?.length) {
  //     return this.orders().filter(o => !!o.isChecked)
  //   } else {
  //     return []
  //   }
  // })

  readonly queryOrders = injectInfiniteQuery(() => {
    const queryKey = this.ordersQueryKey()
    const { sortProp, sortDir, filters: requestFilters } = queryKey[2]
    const schema = this.setSchema()

    return {
      enabled: this.submittedFilters.ready(),
      queryKey,
      queryFn: ({ pageParam }) => {
        const params = {
          page: pageParam,
          sort: sortProp,
          direction: sortDir,
          filters: requestFilters,
        }
        return this.orderService.getOrders(params, schema)
      },
      initialPageParam: 1,
      getNextPageParam: result => {
        if (result && result?.paginator && result?.paginator?.current) {
          return Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
            ? Number(result?.paginator?.current) + 1
            : undefined
        }
        return undefined
      },
      onSuccess: () => {
        if (this.areAllOrdersChecked()) {
          const items = this.queryOrders.data()?.pages?.flatMap(p => p?.items ?? []) ?? []
          this.checkedMap.update(map => {
            const newMap = { ...map }
            for (const item of items) {
              const id = item?.id
              if (id != null && newMap[id] === undefined) {
                newMap[id] = true
              }
            }
            return newMap
          })
        }
      },
    }
  })

  // todo vsechny pages (infinite ??? nebo pagination)
  readonly queryUsers = injectQuery(() => ({
    queryKey: ['users', 'options'],
    queryFn: async () => {
      return await this.userService
        .getUsers(
          { page: 1, sort: 'lastName', direction: 'asc' },
          {
            id: null,
            name: null,
          }
        )
        .then(result => {
          return result?.items
        })
    },
  }))

  isShowingTags = computed(() => this.selectedColumns()?.some(c => c.name === 'tags'))

  areCoreDataLoading = computed(() => {
    return (
      this.queryCheckouts.isFetching() ||
      this.queryDepotsOptions.isFetching() ||
      this.queryPayments.isFetching() ||
      this.queryTags.isFetching() ||
      this.queryCompanyCarriers.isFetching() ||
      this.queryUsers.isFetching()
    )
  })

  constructor(
    protected core: CoreService,
    private route: ActivatedRoute,
    protected changeDetector: ChangeDetectorRef
  ) {
    super(core)
    this.storageKey.set(this.keyService.getStorageKeyForEntity('orders'))
    this.loadSorting('orders')
    this.loadSettings()
    this.translateService.onLangChange.subscribe(() => {
      this.shippingCountriesOptions = [
        { name: 'all', code: 'all' },
        ...GeoConfig.getCountries(this.translateService.currentLang),
      ]
    })
    effect(() => {
      this.queryUsers.data()
      this.queryTags.data()
      this.queryPayments.data()
      this.queryCheckouts.data()
      this.queryCompanyCarriers.data()
      this.queryDepotsOptions.data()
      // this.queryOrders.data()
    })
  }

  async ngOnInit() {
    this.subscription = this.route.queryParams.subscribe(params => {
      // todo test specific scenario
      if (params?.item?.length > 0) {
        // Load filters coming from another listing (sold items). The value may be a JSON string
        // or even a stringified Angular Signal like: [Input Signal: { ... }]. Parse safely.
        const raw = params.stringifiedFilters
        let parsed = structuredClone(DEFAULT_FILTERS)
        if (raw) {
          if (typeof raw === 'string') {
            let text = raw
            // Extract JSON object from possible wrapper like "[Input Signal: {...}]"
            const start = text.indexOf('{')
            const end = text.lastIndexOf('}')
            if (start !== -1 && end !== -1 && end > start) {
              text = text.substring(start, end + 1)
            }
            try {
              parsed = hydrateOrderFilters(JSON.parse(text))
            } catch (e) {
              // Fallback to defaults if parsing fails
              parsed = structuredClone(DEFAULT_FILTERS)
            }
          } else if (typeof raw === 'object') {
            parsed = hydrateOrderFilters(raw)
          }
        }
        // Set filters and override the 'item' field from query param immutably
        this.filters.set({ ...hydrateOrderFilters(parsed), item: params.item })
        this.filtersFromSellItem.set(true)
      }
      this.submittedFilters.commit(this.filters(), this.queryFilters())
      this.refetchOrdersFromFirstPage()
    })
  }

  saveSettings(filters = this.submittedFilters.snapshot()?.view ?? this.filters()): void {
    this.core.services.storage.set(this.storageKey(), {
      selectedColumns: this.selectedColumns().map(c => c.name),
      filters: filters ? JSON.stringify(filters) : undefined,
      sortProp: this.sortProp(),
      sortDir: this.sortDir(),
    })
  }

  loadSettings(): void {
    const view = this.core.services.storage.getSync(this.storageKey())
    if (view?.orders?.selectedColumns?.length) {
      this.selectedColumns.set(
        this.listingColumns().filter(savedColumn => {
          return view?.orders?.selectedColumns.find(column => column === savedColumn.name)
        })
      )
    }
    this.filters.set(hydrateOrderFilters(view?.filters?.length ? JSON.parse(view.filters) : undefined))
    this.sortProp.set(view?.sortProp || 'dateCreated')
    this.sortDir.set(view?.sortDir || 'desc')
    // Do not refetch here; it would run too early (constructor phase). ngOnInit will handle the initial fetch.
  }

  setFilters(property: string, value: any, dateLeftRight?: string) {
    if (property === 'carriers') value = normalizeCarrierIds(value)
    if (dateLeftRight) {
      this.filters.set({
        ...this.filters(),
        [property]: {
          ...this.filters()[property],
          [dateLeftRight]: value,
        },
      })
    } else {
      this.filters.set({
        ...this.filters(),
        [property]: value,
      })
    }
  }

  async ngOnDestroy() {
    if (this.filtersFromSellItem()) this.filtersFromSellItem.set(false)
    this.subscription.unsubscribe()
    this.stopRefreshing()
  }

  private currentRequestId = 0

  public clearFilters() {
    this.filters.set(structuredClone(DEFAULT_FILTERS))
    this.resetSelections()
    this.submitFilters()
  }

  setSchema(): Partial<Order> | any {
    const schema: Partial<Order> | any = {
      id: null,
    }
    if (this.selectedColumns().some(c => c.name === 'status')) {
      schema.status = {
        id: null,
        name: null,
      }
    }
    if (this.selectedColumns().some(c => c.name === 'priority')) {
      schema.priority = null
    }
    if (this.selectedColumns().some(c => c.name === 'order.short')) {
      schema.reservationNumber = null
    }
    if (this.selectedColumns().some(c => c.name === 'bill.short')) {
      schema.billNumber = null
    }
    if (this.selectedColumns().some(c => c.name === 'shipping')) {
      schema.carrier = {
        id: null,
        name: null,
        color: null,
      }
    }
    if (this.selectedColumns().some(c => c.name === 'checkout')) {
      schema.checkout = {
        id: null,
        name: null,
      }
    }
    if (this.selectedColumns().some(c => c.name === 'price')) {
      schema.priceAll = null
      schema.currency = {
        id: null,
        name: null,
        ratio: null,
      }
    }
    if (this.selectedColumns().some(c => c.name === 'payments.method')) {
      schema.paymentItems = {
        id: null,
        payment: { id: null, name: null, type: { id: null, name: null } },
        amount: null,
        isPaid: null,
      }
    }
    if (this.selectedColumns().some(c => c.name === 'datePaid')) {
      schema.paymentItems = {
        ...schema.paymentItems,
        id: null,
        isPaid: null,
        datePaid: null,
      }
    }
    if (this.selectedColumns().some(c => c.name === 'externalReference')) {
      schema.externalReference = null
    }
    if (this.selectedColumns().some(c => c.name === 'orders.dateExpedition')) {
      schema.dateExpedition = null
    }
    if (this.selectedColumns().some(c => c.name === 'dateCreated')) {
      schema.dateCreated = null
    }
    if (this.selectedColumns().some(c => c.name === 'notes')) {
      schema.note = null
      schema.privateNote = null
    }
    if (this.selectedColumns().some(c => c.name === 'orders.createdBy')) {
      schema.createdBy = {
        id: null,
        name: null,
        firstName: null,
        lastName: null,
      }
    }
    if (this.selectedColumns().some(c => c.name === 'orders.customerName')) {
      schema.customer = {
        id: null,
        email: null,
        firstName: null,
        lastName: null,
        name: null,
        companyName: null,
      }
      if (!this.selectedColumns().some(c => c.name === 'country-invoice')) {
        schema.invoiceAddress = {
          id: null,
          firstName: null,
          lastName: null,
          country: null,
          state: null,
        }
      }
    }
    if (this.selectedColumns().some(c => c.name === 'packageInfo')) {
      schema.packages = {
        id: null,
        code: null,
        carrier: { id: null, name: null, color: null, position: null, tariffs: { id: null, name: null } },
        statuses: { id: null, code: null, text: null, created: null },
        disposal: {
          id: null,
          code: null,
          carrier: { id: null, name: null },
          packages: { id: null },
          ticketUrl: null,
          sent: null,
        },
        ticketUrl: null,
        sent: null,
      }
    }
    if (
      this.selectedColumns().some(c => c.name === 'quantityUnavailable') ||
      this.selectedColumns().some(c => c.name === 'icons')
    ) {
      schema.quantityUnavailable = null
    }
    if (this.selectedColumns().some(c => c.name === 'icons')) {
      schema.depots = {
        id: null,
        name: null,
      }
    }
    if (this.selectedColumns().some(c => c.name === 'processStatus')) {
      schema.processStatus = { id: null, name: null, position: null }
      schema.processStatusUpdated = null
    }
    if (this.selectedColumns().some(c => c.name === 'country-invoice')) {
      schema.invoiceAddress = {
        id: null,
        country: null,
        state: null,
        firstName: null,
        lastName: null,
      }
    }
    if (this.selectedColumns().some(c => c.name === 'country-shipping')) {
      schema.shippingAddress = {
        id: null,
        country: null,
        state: null,
      }
    }
    // todo stahovat az p otevreni okna ?
    if (this.selectedColumns().some(c => c.name === 'preview')) {
      schema.note = null
      schema.privateNote = null
    }
    if (this.selectedColumns().some(c => c.name === 'tags')) {
      schema.tags = { id: null, name: null, type: null, color: null, externalId: null }
    }
    return schema
  }

  allOrdersChecked(checked: boolean) {
    const mapSignal = this.checkedMap
    if (!mapSignal) return

    const items = this.orderItems()

    mapSignal.update(map => {
      const newMap = { ...map }
      for (const item of items) {
        newMap[item.id] = checked
      }
      return newMap
    })
    // Keep header state in sync reactively
    this.areAllOrdersChecked.set(checked)
  }

  toggleChecked(id: string, checked: boolean) {
    if (!checked) {
      this.areAllOrdersChecked.set(false)
    }
    this.checkedMap.update(map => ({
      ...map,
      [id]: checked,
    }))
  }

  setCheckedOrdersToFalse() {
    // Reset selection state reactively
    this.areAllOrdersChecked.set(false)
    this.checkedMap.set({})
  }

  areAllOrdersCheckboxCheckedChanged(event) {
    this.areAllOrdersChecked.set(event)
  }

  // Ensure that when "Select All" is active, any newly loaded items (e.g., next pages)
  // are automatically marked as checked. This is reactive and more reliable than
  // relying solely on onSuccess hooks.
  private _autoCheckEffect = effect(() => {
    const isAllOn = this.areAllOrdersChecked()
    const pages = this.queryOrders.data()?.pages ?? []
    if (!isAllOn || !pages?.length) return

    const items = pages.flatMap(p => p?.items ?? [])
    if (!items?.length) return

    this.checkedMap.update(map => {
      // Only set entries that aren't present yet to avoid overriding explicit user choices
      const updated = { ...map }
      for (const it of items) {
        const id = it?.id
        if (id != null && updated[id] === undefined) {
          updated[id] = true
        }
      }
      return updated
    })
  })

  /**
   * Clears any selection state when filters change
   */
  resetSelections() {
    this.areAllOrdersChecked.set(false)
    this.checkedMap.set({})
  }

  /**
   * Unified handler for filter submit actions (parity with Products)
   */
  onFilterSubmit() {
    this.resetSelections()
    this.submitFilters()
  }

  private submitFilters(): void {
    this.submittedFilters.submit(this.filters(), this.queryFilters())
  }

  private refetchOrdersFromFirstPage() {
    // Only list resets discard pages; clear their selections at the same time.
    this.resetSelections()
    trimInfiniteQueryToFirstPage(this.queryClient, this.ordersQueryKey())
    return this.queryOrders.refetch()
  }

  // downloadReport(report: any) {
  //   this.core.services.download.downloadDocumentAndShowInNewWindow(report.url)
  // }

  /**
   * Handles invoice download request coming from FilterFormComponent.
   * Calls backend bulk-export and opens the resulting document.
   */
  public async onDownloadInvoices(event: { type: string; ids: number[] }) {
    this.loadingModal.showChildModal()
    try {
      const payload = {
        type: event.type,
        filter: event.ids?.length
          ? JSON.stringify({ ids: event.ids })
          : JSON.stringify(this.submittedFilters.snapshot()?.request ?? {}),
      }

      await this.core.services.export
        .createBulkPrintOrders(payload, {
          id: null,
          generated: null,
          created: null,
          url: null,
          type: {
            id: null,
          },
          filter: null,
          createdBy: {
            id: null,
          },
          note: null,
        })
        .then(result => {
          this.startRefreshingBulkOrdersExport(result)
        })
    } catch (e) {
      console.error('onDownloadInvoices failed', e)
    }
  }

  startRefreshingBulkOrdersExport(exportToCreate) {
    if (this.refreshInterval) return

    // Reset attempts at the start of a new refresh session
    this.refreshAttemptOrdersExport = 0

    const scheduleNext = () => {
      // Determine delay based on attempt number: 5s, 10s, 15s, then 20s afterwards
      const attempt = this.refreshAttemptOrdersExport
      const base = OptionsConfig.strings.refreshIntervalTimeMS // acts as the max cap (e.g., 20s)
      const delays = [5_000, 10_000, 15_000]
      const delay = attempt < delays.length ? delays[attempt] : base

      this.refreshInterval = setTimeout(() => {
        // Perform the refresh action
        this.core.services.export
          .getById(exportToCreate.id, {
            id: null,
            generated: null,
            created: null,
            url: null,
            type: {
              id: null,
            },
            filter: null,
            createdBy: {
              id: null,
            },
            note: null,
          })
          .then(result => {
            if (result.url?.length) {
              this.loadingModal.hideChildModal()
              this.stopRefreshingBulkOrdersExport()
              this.core.services.download.downloadDocumentAndShowInNewWindow(result.url)
            }
          })
        if (this.refreshInterval) {
          this.refreshAttemptOrdersExport += 1
          scheduleNext()
        }
      }, delay)
    }

    scheduleNext()
  }

  stopRefreshingBulkOrdersExport(): void {
    if (this.refreshInterval) {
      // Clear any pending timer
      clearTimeout(this.refreshInterval)
      // Also clear interval just in case previous versions used it
      clearInterval(this.refreshInterval)
      this.refreshInterval = undefined
    }
    this.refreshAttemptOrdersExport = 0
  }

  // create2(exportObject: any, schema: any = {
  //   id: null,
  //   generated: null,
  //   created: null,
  //   url: null,
  //   type: {
  //     id: null,
  //   },
  //   filter: null,
  //   createdBy: {
  //     id: null,
  //   },
  //   note: null,
  // }): Promise<Export> {
  //   const params: any = {
  //     type: exportObject.type,
  //     filter: exportObject.filter,
  //   }
  //   return this.createUpdateEntity('Export', 'createExport', params, schema)
  // }

  protected readonly DEFAULT_FILTERS = DEFAULT_FILTERS
}
